import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiArrowLeft, FiEye, FiPackage } from "react-icons/fi";
import useStoreProducts from "../../../marketplace_admin/libs/fetchStoreProducts";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import EmptyState from "../../components/EmptyState";
import useInfiniteScrollRef from "../../utils/useInfiniteScrollRef";
import Spinner from "../../../Spinner/Spinner";
import ProductPriceDisplay, { ProductDiscountBadge } from "../../../Routes/Marketplace/components/ProductPriceDisplay";
import { getAdminProductStock } from "../utils/adminProductDisplay";

const PLACEHOLDER = "https://via.placeholder.com/400x300?text=No+Image";

const AdminStoreProductsPage = () => {
  const { merchantId } = useParams();
  const { data, error, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useStoreProducts(merchantId);
  const [search, setSearch] = useState("");
  const lastRef = useInfiniteScrollRef(fetchNextPage, hasNextPage, isFetchingNextPage);

  const products = useMemo(
    () => data?.pages?.flatMap((page) => page.products || []) || [],
    [data]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter(
      (p) =>
        (p.name || "").toLowerCase().includes(term) ||
        (p.category || "").toLowerCase().includes(term) ||
        (p.brand || "").toLowerCase().includes(term)
    );
  }, [products, search]);

  return (
    <>
      <Link to="/admin/merchants" className="dashboard-back-link">
        <FiArrowLeft aria-hidden="true" /> Back to merchants
      </Link>

      <PageHeader
        title="Store products"
        subtitle={`${products.length ? `${products.length} loaded · ` : ""}Merchant ${merchantId?.slice(0, 8).toUpperCase()}`}
        icon={FiPackage}
        actions={
          <Link to={`/admin/merchants/${merchantId}`} className="dashboard-btn dashboard-btn--secondary">
            Merchant profile
          </Link>
        }
      />

      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search products…" />

      {isLoading && <Spinner />}
      {error && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load store products.</p>
        </div>
      )}

      {!isLoading && !error && filtered.length === 0 && (
        <EmptyState
          icon={FiPackage}
          title="No products"
          message={search ? "No products match your search." : "This merchant has not listed any products yet."}
        />
      )}

      <div className="dashboard-product-grid">
        {filtered.map((product, index) => {
          const stock = getAdminProductStock(product);
          const detailUrl = `/admin/merchants/${merchantId}/products/${product._id}`;

          return (
            <article
              key={product._id}
              className="dashboard-card dashboard-product-card admin-store-product-card"
              ref={index === filtered.length - 1 ? lastRef : undefined}
            >
              <Link to={detailUrl} className="admin-store-product-card__media">
                <img src={product.images?.[0] || PLACEHOLDER} alt={product.name} />
                <ProductDiscountBadge product={product} />
                {stock === 0 && (
                  <span className="admin-store-product-card__stock admin-store-product-card__stock--out">
                    Out of stock
                  </span>
                )}
                {stock > 0 && stock <= 5 && (
                  <span className="admin-store-product-card__stock admin-store-product-card__stock--low">
                    Low stock
                  </span>
                )}
              </Link>

              <div className="dashboard-product-card__body">
                <Link to={detailUrl} className="admin-store-product-card__title-link">
                  <h3 className="dashboard-product-card__name">{product.name}</h3>
                </Link>
                <p className="dashboard-product-card__meta">
                  {product.category || "Uncategorized"}
                  {product.brand ? ` · ${product.brand}` : ""}
                </p>
                <ProductPriceDisplay product={product} size="card" className="dashboard-product-card__price" />
                <p className="dashboard-product-card__meta" style={{ margin: "0 0 12px" }}>
                  {stock === 0 ? "No units in stock" : `${stock} units in stock`}
                </p>
                <Link to={detailUrl} className="dashboard-btn dashboard-btn--primary admin-store-product-card__cta">
                  <FiEye aria-hidden="true" /> View full details
                </Link>
              </div>
            </article>
          );
        })}
      </div>

      {!isLoading && filtered.length > 0 && (
        <div className="dashboard-load-more">
          {isFetchingNextPage && <Spinner />}
          {!hasNextPage && !isFetchingNextPage && (
            <span style={{ color: "var(--dash-muted)", fontSize: 13 }}>All products loaded</span>
          )}
        </div>
      )}
    </>
  );
};

export default AdminStoreProductsPage;
